import React, { useMemo, useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLModalService , MLTagManagerService } from 'ml-portals-services-react';
import { MLPortalStorageService } from 'ml-services-common-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { platformMessages } from 'ml-components-platform-react';

import appConfig from 'app-config';

import metadata from './AdditionalInfoToPurchasePagePolChange.metadata.json5';
import messages from './AdditionalInfoToPurchasePagePolChange.messages';
import styles from './AdditionalInfoToPurchasePagePolChange.module.scss';

const {
    ML_STORAGE_KEY_PREVIOUS_PREMIUM
} = appConfig;

const AdditionalInfoToPurchasePagePolChange = (props) => {
    const { wizardData, goNext, updateWizardData } = props;
    const { submissionVM: endorsementVM } = wizardData;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation('PUPAdditionalInformationToPurchasePagePolChange');
    const { refreshData } = useDataRefresh();

    const [showErrors, setShowErrors] = useState(false);
    const [hidePage, setHidePage] = useState(true);

    const {
        showProgressDialog,
    } = MLModalService()

    const getDriverTypeDisplayName = (driverType) => {
        switch (driverType) {
            case 'rated':
                return translator(messages.driver);
            case 'excluded':
                return translator(messages.excludedDriver);
            case 'household':
                return translator(messages.householdMember);
            default:
                break;
        }
    };

    const isDriver = (vehicleOperatorVM) => {
        if (vehicleOperatorVM.driverType.value) {
            switch (vehicleOperatorVM.driverType.value.code) {
                case 'rated':
                case 'excluded':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    };

    const isPrimaryNamedInsured = (vehicleOperatorVM) => vehicleOperatorVM.isPrimaryNamedInsured.value;

    const pupModel = endorsementVM.lobData.personalUmbrella;
    const vehiclesVM = useMemo(() => {
        return pupModel.coverables.vehicles.children;
    }, [pupModel]);

    const sortedVehicleOperatorsML = useMemo(() => {
        const getOperatorSortPriority = (vehicleOperatorVM) => {
            if (isPrimaryNamedInsured(vehicleOperatorVM)) {
                return 0;
            }
            switch (vehicleOperatorVM.driverType.value.code) {
                case 'rated':
                    return 1;
                case 'household':
                    return 2;
                case 'excluded':
                    return 3;
                default:
                    return 9;
            }
        };
        const getSortedOperatorsList = (vehicleOperatorsToSort) => {
        // Sorting by name first and then with Driver's priority to keep the order of the sorted array consistent.
            const nameSortedOperatorsList = vehicleOperatorsToSort.sort((vehicleOperator1VM, vehicleOperator2VM) => {
                const operatorName1 = vehicleOperator1VM.displayName.value ? vehicleOperator1VM.displayName.value.toUpperCase() : '';
                const operatorName2 = vehicleOperator2VM.displayName.value ? vehicleOperator2VM.displayName.value.toUpperCase() : '';
                if (operatorName1 < operatorName2) {
                    return -1;
                }

                if (operatorName1 > operatorName2) {
                    return 1;
                }

                return 0;
            });


            return nameSortedOperatorsList.sort((vehicleOperator1VM, vehicleOperator2VM) => {
                const operator1Priority = getOperatorSortPriority(vehicleOperator1VM);
                const operator2Priority = getOperatorSortPriority(vehicleOperator2VM);

                if (operator1Priority < operator2Priority) {
                    return -1;
                }

                if (operator1Priority > operator2Priority) {
                    return 1;
                }

                return 0;
            });
        };

        const operatorsToSort = [...pupModel.coverables.vehicleOperators.children];
        const operatorList = getSortedOperatorsList(operatorsToSort);
        const sortedOperatorList = [...operatorList].map((operator) => operator.value);

        _.set(endorsementVM, `lobData.personalUmbrella.coverables.vehicleOperators.value`, sortedOperatorList);

        return getSortedOperatorsList(operatorList);
    }, [pupModel,endorsementVM]);

    const vehicleOperatorsVM = useMemo(() => {
        return sortedVehicleOperatorsML.map((vehicleOperatorVM) => {
            vehicleOperatorVM.contact.updated.value = true;
            vehicleOperatorVM.isAgeFirstLicensedRequired.value = true;
            vehicleOperatorVM.isOccupationTypeRequired.value = true;
            vehicleOperatorVM.isEmailAddressRequired.value = vehicleOperatorVM.commPreference.value === 'electronic';
    
            if (isDriver(vehicleOperatorVM)) {
                vehicleOperatorVM.isLicenseNumberRequired.value = true;
                vehicleOperatorVM.isLicenseStateRequired.value = true;
            } else {
                vehicleOperatorVM.isLicenseNumberRequired.value = false;
                vehicleOperatorVM.isLicenseStateRequired.value = false;
            }
    
            if (vehicleOperatorVM.isPrimaryNamedInsured.value) {
                vehicleOperatorVM.isHasLossWithin5YearsRequired.value = true;
            } else {
                vehicleOperatorVM.isHasLossWithin5YearsRequired.value = false;
            }
    
            vehicleOperatorVM.value.hasEmailAddressInput = !!vehicleOperatorVM.emailAddress.value;
            vehicleOperatorVM.editMode_ML.value = true;
            return vehicleOperatorVM;
        });
    }, [sortedVehicleOperatorsML]);

    const watercraftOperatorsVM = useMemo(() => {
        return pupModel.coverables.watercraftOperators.children.map((watercraftOperatorVM) => {
            watercraftOperatorVM.isYearsOfExperienceRequired.value = true;
            return watercraftOperatorVM;
        });
    }, [pupModel]);

    const isVehiclePrivateOrClassic = (vehicleVM) => {
        switch (vehicleVM.type.value.code) {
            case 'private_passenger_autos_ml':
            case 'collectible_ml':
            case 'vans_suvs_ml':
            case 'pickups_ml':
            case 'panel_trucks_ml':
            case 'flat_bed_box_truck_ml':
                return true;
            default:
                return false;
        }
    };

    const getVehicleYearMakeName = (vehicleVM) => {
        return vehicleVM.value.getYearMakeNameML();
    };

    const getVehicleModelTrimName = (vehicleVM) => {
        if (isVehiclePrivateOrClassic(vehicleVM)) {
            return `${vehicleVM.value.getModelNameML()} ${vehicleVM.value.getTrimNameML()}`;
        }
        return vehicleVM.value.getModelNameML();
    };

    const isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal = (vehicleVM) => {
        switch (vehicleVM.type.value.code) {
            case 'trailer_ml':
            case 'GolfCart_ML':
            case 'LowSpeed_ML':
            case 'atv_snowmobile_ml':
            case 'Other_Personally_Registered':
                return true;
            default:
                return false;
        }
    };

    const getPersonFullNameFormatted = (vehicleOperatorVM) => {
        if (vehicleOperatorVM.value && vehicleOperatorVM.contact.value) {
            return vehicleOperatorVM.contact.value.displayFullName_Ext;
        }
    };

    const isEmailAddressVisible = (vehicleOperatorVM) => {
        let isVisible;
        if (vehicleOperatorVM.isPrimaryNamedInsured.value && vehicleOperatorVM.commPreference.value.code === 'electronic') {
            isVisible = true;
        } else if (vehicleOperatorVM.contact.value.address.state === 'CO' && endorsementVM.value.isPersonalAuto() && vehicleOperatorVM.contact.maritalStatus.value.code === 'M') {
            isVisible = true;
        } else if ((vehicleOperatorVM.contact.value.address.state === 'GA' || vehicleOperatorVM.contact.value.address.state === 'UT') && vehicleOperatorVM.driverType.value.code === 'excluded') {
            isVisible = true;
        } else {
            isVisible = false;
        }
        return isVisible;
    };
    
    const isVinDuplicated = useCallback((currentVehicle) => {
        const vehicles = _.get(endorsementVM, 'lobData.personalUmbrella.coverables.vehicles.children', []);
        const vehicleVin = currentVehicle.vin;
        const vehiclesWithTheSameVin = vehicles ? vehicles.filter(
            (x) => !_.isUndefined(x.vin.value) && x.vin.value === vehicleVin.value
        ) : [];
        return vehiclesWithTheSameVin.length >= 2;
    }, [endorsementVM]);


    const checkIfVinsVerified = () => {
        return !vehiclesVM.some((vehicle) => {
            return (!isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) && !vehicle.isValidVin);
        });
    };

    const isPageValid = () => {
        if (!endorsementVM.aspects.valid || !endorsementVM.aspects.subtreeValid) {
            return false;
        }

        return true;
    };

    const checkAllRequiredVinsLookedUp = () => {
        return vehiclesVM.every((vehicle) => {
            return (isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicle) || !_.isNil(vehicle.isValidVin));
        });
    };

    const saveAndQuote = () => {
        const promise = EndorsementService.saveAndQuote(endorsementVM.value, authHeader)
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            MLTagManagerService.sendFormStepPolicyChangeStep();
            endorsementVM.value = new PolicyChange(responseData);
            updateWizardData(wizardData);
            goNext();
        });
    };

    const savePremiumAndGoToNext = () => {
        const jsonData = {total: endorsementVM.value.totalCost};
        MLPortalStorageService.store(endorsementVM.value.jobID, null, ML_STORAGE_KEY_PREVIOUS_PREMIUM, JSON.stringify(jsonData), authHeader).then(()=>{
            saveAndQuote();
        });
    };

    const verifyVinsOnLoad = () => {
        const intervalPromise = new Promise((resolve, reject) => {
            let repetitions = 12;
            const vinInterval = setInterval(() => {
                if (checkAllRequiredVinsLookedUp()) {
                    clearInterval(vinInterval);
                    if (checkIfVinsVerified()) {
                        resolve();
                    } else {
                        reject();
                    }
                }

                repetitions -= 1;
                if (repetitions === 0) {
                    clearInterval(vinInterval);
                    reject();
                }
            }, 250);
        });    
        showProgressDialog(messages.verifyingVins, intervalPromise, true, 0);
        intervalPromise.then(() => {
            savePremiumAndGoToNext();
        }, () => {
            setHidePage(false)
        });
    };

    const verifyAndSkipPage = () => {
        if (isPageValid()) {
            verifyVinsOnLoad();
            return;
        }    
        setHidePage(false);            
    };

    useEffect(() => {
        verifyAndSkipPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = () => {
        if (isPageValid() && checkIfVinsVerified() && isComponentValid) {
            savePremiumAndGoToNext();
        } else {
            setShowErrors(true);
        }
    };

    const getVehiclesOverrides = () => {
        const overrides = vehiclesVM.map((vehicleVM, index) => {
            return {
                [`vehicleCount${index}`]: {
                    content: translator(messages.vehicleX, { vehicleNumber: index+1 })
                },
                [`vehicleName${index}`]: {
                    content: (
                        <>
                            <strong>{ getVehicleYearMakeName(vehicleVM) }</strong> { getVehicleModelTrimName(vehicleVM) }
                        </>
                    )                    
                },
                [`vinContainer${index}`]: {
                    visible: !isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                },
                [`vinLookupComponent${index}`]: {
                    vehicleVM,
                    quoteID: endorsementVM.jobID,
                    path: `lobData.personalUmbrella.coverables.vehicles.children[${index}]`,
                    isVinDuplicated,
                    onValueChange: (value, path) => {
                        _.set(endorsementVM, path, value);
                        refreshData();
                    },
                    updateIsValidVin: (vinValidFlag) => {
                        vehicleVM.isValidVin = vinValidFlag;
                    },
                    index,
                    onValidate,
                    showValidationErrors: showErrors,
                    updateVehicleDetails: refreshData,
                    isPUP: true
                },
                [`serialNumberContainer${index}`]: {
                    visible: isCategoryTrailerGolfCartOrLowSpeedOrOtherPersonal(vehicleVM)
                }
            }          
        });

        return Object.assign({}, ...overrides);
    };

    const getVehicleOperatorsOverrides = () => {
        const overrides = vehicleOperatorsVM.map((vehicleOperatorVM, index) => {
            return {
                [`vehicleOperatorCount${index}`]: {
                    content: `${getDriverTypeDisplayName(vehicleOperatorVM.driverType.value.code)} ${translator(messages.vehicleOperatorX, { vehicleOperatorNumber: index+1})}`
                },
                [`vehicleOperatorName${index}`]: {
                    content: <strong>{ getPersonFullNameFormatted(vehicleOperatorVM) }</strong>
                },
                [`emailContainer${index}`]: {
                    visible: isEmailAddressVisible(vehicleOperatorVM)
                },
                [`licenseNumberContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`licenseStateContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`ageFirstLicensedContainer${index}`]: {
                    visible: isDriver(vehicleOperatorVM)
                },
                [`hasLossContainer${index}`]: {
                    visible: vehicleOperatorVM.isPrimaryNamedInsured.value === true
                },
                [`vehicleOperatorLineSeparator${index}`]: {
                    visible: index !== (vehicleOperatorsVM.length - 1) || watercraftOperatorsVM.length > 0
                },
                [`vehicleOperatorLineEndSeparator${index}`]: {
                    visible: index === (vehicleOperatorsVM.length - 1) && !(watercraftOperatorsVM.length > 0)
                }
            }          
        });

        return Object.assign({}, ...overrides);
    }

    const getWatercraftOperatorsOverrides = () => {
        const overrides = watercraftOperatorsVM.map((watercraftOperatorVM, index) => {
            return {
                [`watercraftOperatorCount${index}`]: {
                    content: translator(messages.watercraftOperatorX, { watercraftOperatorNumber: index+1})
                },
                [`watercraftOperatorName${index}`]: {
                    content: <strong>{ getPersonFullNameFormatted(watercraftOperatorVM) }</strong>
                }
            }          
        });

        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        pageContainer: {
            className: hidePage ? styles.displayNone : ''
        },
        vehiclesIterable: {
            data: vehiclesVM
        },
        vehicleOperatorsIterable: {
            data: vehicleOperatorsVM
        },
        vehicleOperatorsIterable2: {
            data: vehicleOperatorsVM
        },
        watercraftOperatorsIterable: {
            data: watercraftOperatorsVM
        },
        nextDescription: {
            content: (
                <FormattedMessage 
                    {...messages.nextDesc}
                    values={{
                        reviewAdditionalActivity: <strong>{ translator(messages.reviewAdditionalActivity) }</strong>,
                        mvrsMayBeRun: <strong>{ translator(messages.mvrsMayBeRun) }</strong>
                    }}
                />
            )
        },
        ...getVehiclesOverrides(),
        ...getVehicleOperatorsOverrides(),
        ...getWatercraftOperatorsOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    }

    return (
        <WizardPage
            policyChangePageHeaderML={messages.additionalDetailsHeader}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                model={endorsementVM}
                onModelChange={refreshData}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
        </WizardPage>
    );
}

AdditionalInfoToPurchasePagePolChange.propTypes = wizardProps;
export default AdditionalInfoToPurchasePagePolChange;